import { DialogProps } from "@material-ui/core/Dialog";
import * as React from "react";

import { JobCategoryEnum } from ".";
import {
    ChipSelectionDialog,
    createError,
    IChip,
    RelodeApiError,
    useRelodeApp,
} from "../../";
import { JobPosition } from "../../domain";
import { useDeviceSize } from "../hooks";
import { MobileJobPositionDialog } from "./";

const convertPositionsToMatchableChips = (
    jobPositions?: JobPosition[],
    selectedItems?: IChip[],
    excludedItemId?: number
) => {
    const chipsTags = {};
    Object.keys(JobCategoryEnum).forEach(key => {
        chipsTags[JobCategoryEnum[key].id] = [];
    });

    jobPositions &&
        jobPositions.forEach(position => {
            if (position.jobCategoryId) {
                const values = chipsTags[position.jobCategoryId];

                if (values && excludedItemId !== position.jobPositionId) {
                    const selectedItem =
                        selectedItems &&
                        selectedItems.find(
                            sItem => sItem.id === position.jobPositionId
                        );

                    values.push({
                        id: position.jobPositionId,
                        name: position.jobPositionName,
                        groupId: position.jobCategoryId,
                        groupName: position.jobCategoryName,
                        isMatched: !!selectedItem,
                        canMatch: true,
                        canEdit: true,
                    });
                    chipsTags[position.jobCategoryId] = values;
                }
            }
        });
    return chipsTags;
};

const convertCategoriesToChips = (
    availableJobCategories: { name: string; id: number }[]
): IChip[] => {
    let chipArray = [];
    availableJobCategories
        ? (chipArray = Object.keys(availableJobCategories).map(key => ({
              id: availableJobCategories[key].id,
              name: availableJobCategories[key].name,
              isMatched: false,
              canEdit: true,
              canMatch: true,
          })))
        : (chipArray = Object.keys(JobCategoryEnum).map(key => ({
              id: JobCategoryEnum[key].id,
              name: JobCategoryEnum[key].name,
              isMatched: false,
              canEdit: true,
              canMatch: true,
          })));
    return chipArray;
};

const convertChipsToJobPositions = (chips: IChip[]): JobPosition[] => {
    const jobPositions: JobPosition[] = [];
    chips &&
        chips.forEach(chip => {
            const { id, name, groupId, groupName } = chip;
            jobPositions.push({
                jobPositionId: id,
                jobPositionName: name,
                jobCategoryId: groupId || -1,
                jobCategoryName: groupName || "",
            });
        });
    return jobPositions;
};

type Props = DialogProps & {
    baseId: string;
    singleSelect?: boolean;
    groupSelect?: boolean;
    open: boolean;
    onClose: () => void;
    onAdd: (selectedJobPositions: JobPosition[]) => void;
    excludedItemId?: number;
    // fullScreen?: boolean;
    // device?: "mobile" | "desktop";
    availableJobCategories?: { name: string; id: number }[];
};

export const AddJobPositionDialog = (props: Props) => {
    const {
        state: {
            config: { jobPositionLookupService },
        },
    } = useRelodeApp();
    const {
        baseId,
        onClose,
        open,
        singleSelect,
        groupSelect,
        // fullScreen,
        // device,
        availableJobCategories,
    } = props;

    const [suggestions, setSuggestions] = React.useState<JobPosition[]>([]);
    const device = useDeviceSize();
    const extendedId = `${baseId}AddJobPositionDialog`;

    const lookupJobPositionsSuggestion = async (
        searchText: string,
        jobCategoryIds: number[],
        selectedItems?: IChip[]
    ) => {
        const newCriteria = { searchText, jobCategoryIds };
        try {
            const response = await jobPositionLookupService.loadSuggestions(
                newCriteria
            );
            setSuggestions(response);
            return convertPositionsToMatchableChips(
                response,
                selectedItems,
                props.excludedItemId
            );
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort() || err.isClientError()) return;
            }
            throw createError(
                err,
                "core.addJobPositionDialog.lookupJobPositionsSuggestion"
            );
        }
    };

    const pruneSelectedJobPositions = (selectedChips: IChip[]) => {
        if (suggestions === undefined) {
            return [];
        } else {
            const selected = selectedChips?.filter(t =>
                suggestions.find(s => s.jobPositionId === t.id)
            );
            return selected;
        }
    };

    const onAddJobPosition = (selectedChips: IChip[]) => {
        const selectedTags = convertChipsToJobPositions(selectedChips);
        props.onAdd(selectedTags);
    };

    return (
        <>
            {device === "mobile" ? (
                <MobileJobPositionDialog
                    baseId={extendedId}
                    closeOnAdd
                    singleSelect={singleSelect}
                    open={open}
                    chipGroups={convertCategoriesToChips(
                        availableJobCategories
                    )}
                    searchSuggestions={lookupJobPositionsSuggestion}
                    pruneSelectedChips={pruneSelectedJobPositions}
                    onAdd={onAddJobPosition}
                    onClose={onClose}
                    context={singleSelect ? "job position" : "job positions"}
                />
            ) : (
                <ChipSelectionDialog
                    baseId={extendedId}
                    closeOnAdd
                    singleSelect={singleSelect}
                    groupSelect={groupSelect}
                    open={open}
                    chipGroups={convertCategoriesToChips(
                        availableJobCategories
                    )}
                    searchSuggestions={lookupJobPositionsSuggestion}
                    pruneSelectedChips={pruneSelectedJobPositions}
                    onAdd={onAddJobPosition}
                    onClose={onClose}
                    context={singleSelect ? "job position" : "job positions"}
                />
            )}
        </>
    );
};
